import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useLogoutWithConfirm } from "@/components/logout-confirm";
import { formatGrams, formatNumber, focusRing } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { WeekPlan } from "@/types/meal-plan-state";
import { useWeekPlan } from "@/context/week-plan-context";
import { useFriends } from "@/context/friends-context";
import { useAuth } from "@/context/auth-context";
import {
  fetchWeekSnapshot,
  reverseGeocodePlazaVeaLocation,
  searchStoreProducts,
  type PlazaVeaCoverageDistrict,
  type StoreCart,
  type StoreCartProduct,
  type StoreDeliveryLocation,
  type StoreMapping,
  type StoreProduct,
} from "@/lib/api";
import {
  storeQueryKeys,
  useAddStoreMappedCartItems,
  useConnectStore,
  useDeleteStoreMapping,
  useDisconnectStore,
  usePlazaVeaCoverage,
  usePlazaVeaLocation,
  useRemoveStoreCartItem,
  useSaveStoreMapping,
  useSetPlazaVeaLocation,
  useSetStoreCartItemQuantity,
  useSetStoreCartSkuQuantity,
  useSetWongLocation,
  useStoreCart,
  useStoreMappings,
  useStoreStatus,
  useWongLocation,
} from "@/lib/store-queries";
import { STORE_META, otherStore, type StoreId } from "@/lib/stores";
import { useQueryClient } from "@tanstack/react-query";
import { migrateStoredWeek } from "@/lib/plan-switch";
import { flushPendingSaves } from "@/lib/pending-saves";
import { aggregateWeek, mergeAggregatedItems, type AggregatedItem } from "@/lib/shopping-aggregate";
import { formatShoppingAmount, unitLabel } from "@/lib/shopping-format";
import { defaultConversionFor } from "@/lib/store-conversion";
import {
  includedFriendIds,
  loadComprasFriendPrefs,
  saveComprasFriendPrefs,
  setIncluded,
  type ComprasFriendPrefs,
} from "@/lib/compras-friends";
import { ListXIcon, PlusIcon, SearchIcon, ShoppingCartIcon, Trash2Icon, XIcon } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ProgressDots } from "@/components/ui/progress-dots";
import { ComprasToast, useComprasToast } from "@/components/ui/compras-toast";
import {
  StoreContextStrip,
  type StoreConnectionTone,
} from "@/components/meal-plan/store-context-strip";
import { DisconnectConfirmDialog, StoreSheet, type SheetConnectionState } from "@/components/meal-plan/store-sheet";
import { ProgressMeter, type ProgressMeterTone } from "@/components/meal-plan/progress-meter";
import { ComprasActionBar, type ActionBarVariant } from "@/components/meal-plan/compras-action-bar";
import { CartLineCompact, IngredientRow } from "@/components/meal-plan/ingredient-row";

function toCartItem(item: AggregatedItem) {
  return {
    ingredientName: item.name,
    totalGrams: item.totalGrams,
    totalUnits: item.totalUnits,
    requiredAmountLabel: formatShoppingAmount(item),
  };
}

function plazaDistrictLabel(district: PlazaVeaCoverageDistrict) {
  const parts = [district.displayName, district.provinceName, district.departmentName].filter(Boolean);
  return parts.join(", ");
}

function normalizeSearch(value: string) {
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

function districtMatchesQuery(district: PlazaVeaCoverageDistrict, query: string) {
  const normalized = normalizeSearch(query);
  if (!normalized) return true;
  return normalizeSearch(plazaDistrictLabel(district)).includes(normalized) ||
    normalizeSearch(district.displayName).includes(normalized) ||
    district.ubigeo.includes(normalized);
}

function findDistrictByName(districts: PlazaVeaCoverageDistrict[], name: string | null) {
  if (!name) return null;
  const normalized = normalizeSearch(name);
  return districts.find((district) => normalizeSearch(district.displayName) === normalized) ??
    districts.find((district) => normalizeSearch(district.name) === normalized) ??
    districts.find((district) => normalizeSearch(plazaDistrictLabel(district)).includes(normalized)) ??
    null;
}

function parseUserCoordinates(
  latitudeStr: string,
  longitudeStr: string,
): { latitude: number; longitude: number } | null {
  if (!latitudeStr.trim() || !longitudeStr.trim()) return null;
  const latitude = Number(latitudeStr);
  const longitude = Number(longitudeStr);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return null;
  // Reject (0, 0) — almost certainly a misparse from empty inputs. The
  // Atlantic-ocean coordinate has no warehouse, so PlazaVea can't compute
  // delivery SLAs and every item in the cart shows as "agotado" at checkout.
  if (latitude === 0 && longitude === 0) return null;
  return { latitude, longitude };
}

function firstStoreCoordinates(district: PlazaVeaCoverageDistrict): { latitude: number; longitude: number } | null {
  for (const store of district.stores) {
    if (store.latitude !== null && store.longitude !== null) {
      return { latitude: store.latitude, longitude: store.longitude };
    }
  }
  return null;
}

// Client mirror of `worker/vtex.ts:calculateCartQuantity`. Used by the
// picker's "Agregar sin vincular" path so we can compute the cart quantity
// before any mapping is saved. Keep these two in sync.
function computeQuantityForConversion(input: {
  conversionMode: "measurable" | "grouped-units";
  packageAmount: number | null;
  packageUnit: string | null;
  unitsPerSku: number | null;
  measurementUnit: string;
  unitMultiplier: number;
  totalGrams: number;
  totalUnits: number;
}): number {
  if (input.conversionMode === "grouped-units") {
    const ups = input.unitsPerSku ?? 0;
    if (ups <= 0) return 1;
    const required = input.totalUnits > 0 ? input.totalUnits : 1;
    return Math.max(1, Math.ceil(required / ups));
  }
  const mu = (input.measurementUnit || "").toLowerCase();
  if (mu === "kg" && input.unitMultiplier > 0 && input.totalGrams > 0) {
    return Math.max(1, Math.ceil(input.totalGrams / 1000 / input.unitMultiplier));
  }
  const pu = input.packageUnit?.toLowerCase();
  if (input.packageAmount && pu && input.totalGrams > 0) {
    const grams = pu === "kg" ? input.packageAmount * 1000 : pu === "g" ? input.packageAmount : 0;
    if (grams > 0) return Math.max(1, Math.ceil(input.totalGrams / grams));
  }
  return 1;
}

// Mirror of worker `calculateCartQuantity` for client-side V2 sufficiency
// checks. Must stay in sync with `vtex.ts:calculateCartQuantity`.
function computeAmountProvidedByCart(mapping: StoreMapping, quantityInCart: number): {
  grams: number;
  units: number;
} {
  if (mapping.conversionMode === "grouped-units") {
    return { grams: 0, units: (mapping.unitsPerSku ?? 0) * quantityInCart };
  }
  const measurementUnit = mapping.measurementUnit.toLowerCase();
  if (measurementUnit === "kg" && mapping.unitMultiplier > 0) {
    return { grams: mapping.unitMultiplier * 1000 * quantityInCart, units: 0 };
  }
  const packageUnit = mapping.packageUnit?.toLowerCase();
  const packageAmount = mapping.packageAmount ?? 0;
  if (packageAmount > 0 && packageUnit === "kg") {
    return { grams: packageAmount * 1000 * quantityInCart, units: 0 };
  }
  if (packageAmount > 0 && packageUnit === "g") {
    return { grams: packageAmount * quantityInCart, units: 0 };
  }
  return { grams: 0, units: 0 };
}

interface CartSufficiency {
  status: "sufficient" | "insufficient" | "unknown";
  missingLabel: string | null;
}

function computeCartSufficiency(
  item: AggregatedItem,
  mapping: StoreMapping,
  quantityInCart: number,
): CartSufficiency {
  const provided = computeAmountProvidedByCart(mapping, quantityInCart);
  if (item.totalGrams > 0) {
    const need = item.totalGrams;
    const have = provided.grams;
    if (have <= 0) return { status: "unknown", missingLabel: null };
    if (have >= need) return { status: "sufficient", missingLabel: null };
    return { status: "insufficient", missingLabel: `Faltan ${formatGrams(need - have)}` };
  }
  if (item.totalUnits > 0) {
    const need = item.totalUnits;
    const have = provided.units;
    if (have <= 0) return { status: "unknown", missingLabel: null };
    if (have >= need) return { status: "sufficient", missingLabel: null };
    return { status: "insufficient", missingLabel: `Faltan ${formatNumber(need - have)} ${unitLabel(item.unit, need - have)}` };
  }
  return { status: "sufficient", missingLabel: null };
}

const CATEGORY_LABELS: Record<string, string> = {
  protein: "Proteínas",
  carb: "Carbohidratos",
  fruit: "Frutas",
  vegetable: "Vegetales",
  fat: "Grasas",
};

const CATEGORY_ORDER: Record<string, number> = {
  protein: 0,
  carb: 1,
  fruit: 2,
  vegetable: 3,
  fat: 4,
};


// "Bought" = the user took care of the ingredient for this week, which the
// app also sets automatically after adding it to a cart. That makes it
// store-specific: an ingredient in the Wong cart isn't handled for PlazaVea.
function getBoughtKey(store: StoreId, weekId: string): string {
  return `bought:${store}:${weekId}`;
}

// Before multi-store support the key had no store and every cart was PlazaVea.
function getLegacyBoughtKey(weekId: string): string {
  return `bought:${weekId}`;
}

function loadBoughtItems(store: StoreId, weekId: string): Set<string> {
  try {
    const raw =
      localStorage.getItem(getBoughtKey(store, weekId)) ??
      (store === "plazavea" ? localStorage.getItem(getLegacyBoughtKey(weekId)) : null);
    if (raw) return new Set(JSON.parse(raw));
  } catch {}
  return new Set();
}

function saveBoughtItems(store: StoreId, weekId: string, items: Set<string>) {
  localStorage.setItem(getBoughtKey(store, weekId), JSON.stringify([...items]));
}

// ── Picker & Extras dialogs (preserved from previous implementation) ──

interface StoreExtrasDialogProps {
  store: StoreId;
  weekId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: (message: string) => void;
}

function StoreExtrasDialog({ store, weekId, open, onOpenChange, onAdded }: StoreExtrasDialogProps) {
  const storeLabel = STORE_META[store].label;
  const setSkuQuantityMutation = useSetStoreCartSkuQuantity(store);
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<StoreProduct | null>(null);
  const [quantity, setQuantity] = useState("1");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setProducts([]);
      setSelectedProduct(null);
      setQuantity("1");
      setError(null);
    }
  }, [open]);

  async function runSearch() {
    const trimmed = query.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    try {
      setProducts(await searchStoreProducts(store, trimmed));
    } catch (err) {
      setError(err instanceof Error ? err.message : `No se pudo buscar en ${storeLabel}.`);
    } finally {
      setLoading(false);
    }
  }

  async function addProduct() {
    if (!selectedProduct) return;
    const qty = Math.max(1, Math.floor(Number(quantity) || 1));
    setError(null);
    try {
      await setSkuQuantityMutation.mutateAsync({
        weekId,
        skuId: selectedProduct.skuId,
        sellerId: selectedProduct.sellerId,
        quantity: qty,
      });
      onAdded(`${selectedProduct.productName} agregado al carrito.`);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo agregar al carrito.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex flex-col gap-0 p-0 max-h-[95vh] sm:max-h-[95vh]",
          "fixed inset-x-0 bottom-0 top-auto w-full max-w-none translate-x-0 translate-y-0 rounded-t-2xl border-t border-x border-b-0",
          "sm:bottom-auto sm:top-1/2 sm:left-1/2 sm:max-w-2xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:border-b",
          "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100",
          "sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0",
          "sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95",
        )}
      >
        {/* Sticky header — title, X close, search input. Never scrolls. */}
        <header className="flex shrink-0 flex-col gap-3 border-b bg-background px-5 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate text-base font-semibold">
                Agregar producto al carrito
              </DialogTitle>
              <DialogDescription className="truncate text-xs">
                Busca cualquier producto en {storeLabel} y agrégalo al carrito de esta semana.
              </DialogDescription>
            </div>
            <DialogClose asChild>
              <button
                type="button"
                aria-label="Cerrar"
                className={cn(
                  "grid size-10 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted",
                  focusRing,
                )}
              >
                <XIcon className="size-4" />
              </button>
            </DialogClose>
          </div>
          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              void runSearch();
            }}
          >
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar producto"
              className="h-9"
            />
            <Button type="submit" size="sm" disabled={loading || !query.trim()}>
              <SearchIcon className="size-4" />
              Buscar
            </Button>
          </form>
        </header>

        {/* Scrollable product list */}
        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
          <div className="grid gap-2 md:grid-cols-2">
            {products.map((product) => {
              const active = selectedProduct?.skuId === product.skuId;
              return (
                <button
                  type="button"
                  key={product.skuId}
                  onClick={() => setSelectedProduct(product)}
                  className={cn(
                    "grid grid-cols-[72px_1fr] gap-3 rounded-md border p-2 text-left transition-colors",
                    active ? "border-primary bg-primary/5" : "hover:bg-muted/50",
                    focusRing,
                  )}
                >
                  {product.imageUrl ? (
                    <img src={product.imageUrl} alt="" className="size-[72px] rounded border object-contain" />
                  ) : (
                    <span className="size-[72px] rounded border bg-muted" />
                  )}
                  <span className="min-w-0 space-y-1">
                    <span className="block text-sm font-medium">{product.productName}</span>
                    <span className="block text-xs text-muted-foreground">
                      {product.brand || product.sellerName || storeLabel} · SKU {product.skuId}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      S/ {product.price.toFixed(2)}
                    </span>
                    <span className={`block text-xs ${product.isAvailable ? "text-emerald-700" : "text-muted-foreground"}`}>
                      {product.isAvailable ? `Disponible (${product.availableQuantity})` : "No disponible"}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          {loading && <p className="text-sm text-muted-foreground">Buscando productos...</p>}
          {!loading && products.length === 0 && query && (
            <p className="text-sm text-muted-foreground">Sin resultados.</p>
          )}
        </div>

        {/* Sticky footer — selection summary + quantity + add button. */}
        <footer className="shrink-0 border-t bg-background px-5 py-3 safe-area-bottom">
          {selectedProduct ? (
            <div className="space-y-2">
              <p className="truncate text-[12px] text-muted-foreground">
                <span className="font-medium text-foreground">{selectedProduct.productName}</span>
              </p>
              <div className="flex items-center gap-2">
                <Label htmlFor="extras-qty" className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Cantidad
                </Label>
                <Input
                  id="extras-qty"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  step="1"
                  value={quantity}
                  onChange={(event) => setQuantity(event.target.value)}
                  className="h-9 w-20"
                />
                <Button
                  type="button"
                  size="lg"
                  className="ml-auto h-11"
                  onClick={() => void addProduct()}
                  disabled={setSkuQuantityMutation.isPending}
                >
                  {setSkuQuantityMutation.isPending ? <ProgressDots aria-label="Agregando" /> : (
                    <>
                      <ShoppingCartIcon className="size-4" />
                      Agregar al carrito
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-center text-sm text-muted-foreground">
              Elige un producto para agregarlo al carrito.
            </p>
          )}
        </footer>
      </DialogContent>
    </Dialog>
  );
}

interface StoreProductPickerProps {
  store: StoreId;
  item: AggregatedItem | null;
  mapping: StoreMapping | null;
  // The same ingredient's link in the other store, shown as a reference when
  // re-linking after switching supermarkets (pack sizes rarely match 1:1).
  otherStoreMapping: StoreMapping | null;
  existingCartSkuId: string | null;
  weekId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCartResult: (message: string) => void;
  onCartChanged: () => void;
}

function StoreProductPicker({
  store,
  item,
  mapping,
  otherStoreMapping,
  existingCartSkuId,
  weekId,
  open,
  onOpenChange,
  onCartResult,
  onCartChanged,
}: StoreProductPickerProps) {
  const storeLabel = STORE_META[store].label;
  const queryClient = useQueryClient();
  const saveMappingMutation = useSaveStoreMapping(store);
  const deleteMappingMutation = useDeleteStoreMapping(store);
  const addToCartMutation = useAddStoreMappedCartItems(store);
  const removeCartMutation = useRemoveStoreCartItem(store);
  const setSkuQuantityMutation = useSetStoreCartSkuQuantity(store);
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<StoreProduct | null>(null);
  const [conversionMode, setConversionMode] = useState<StoreMapping["conversionMode"]>("measurable");
  const [packageAmount, setPackageAmount] = useState("");
  const [packageUnit, setPackageUnit] = useState("g");
  const [unitsPerSku, setUnitsPerSku] = useState("1");
  // Initialize to `true` so the skeleton renders before the first runSearch
  // resolves; otherwise "Sin resultados" flashes for a frame on each open.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !item) return;
    setQuery(item.name);
    setError(null);
    setProducts([]);
    setSelectedProduct(null);
    setConversionMode(mapping?.conversionMode ?? "measurable");
    setPackageAmount(mapping?.packageAmount ? String(mapping.packageAmount) : "");
    setPackageUnit(mapping?.packageUnit ?? "g");
    setUnitsPerSku(mapping?.unitsPerSku ? String(mapping.unitsPerSku) : "1");
    setLoading(true);
    void runInitialSearch(item.name);
  }, [open, item?.name, store]);

  // First search when the picker opens. Plan ingredient names carry
  // qualifiers stores don't use ("Huevo entero", "Avena cruda (cocinar)"), and
  // store search engines match those qualifiers instead of the food (Wong
  // returns "Pollo Entero" for "Huevo entero"). Drop parentheticals, and if no
  // result even mentions the main word, retry with just that word. The search
  // box shows the query actually used, so the user can see and edit it.
  async function runInitialSearch(ingredientName: string) {
    const cleaned = ingredientName.replace(/\([^)]*\)/g, " ").replace(/\s+/g, " ").trim() || ingredientName;
    const mainWord = cleaned.split(" ")[0] ?? "";
    setQuery(cleaned);
    setLoading(true);
    setError(null);
    try {
      let results = await searchStoreProducts(store, cleaned);
      if (mainWord !== cleaned && mainWord.length >= 3) {
        const stem = normalizeSearch(mainWord).replace(/e?s$/, "");
        if (!results.some((product) => normalizeSearch(product.productName).includes(stem))) {
          const fallback = await searchStoreProducts(store, mainWord);
          if (fallback.length > 0) {
            results = fallback;
            setQuery(mainWord);
          }
        }
      }
      setProducts(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : `No se pudo buscar en ${storeLabel}.`);
    } finally {
      setLoading(false);
    }
  }

  async function runSearch(nextQuery = query) {
    const trimmed = nextQuery.trim();
    if (!trimmed) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setProducts(await searchStoreProducts(store, trimmed));
    } catch (err) {
      setError(err instanceof Error ? err.message : `No se pudo buscar en ${storeLabel}.`);
    } finally {
      setLoading(false);
    }
  }

  function selectProduct(product: StoreProduct) {
    setSelectedProduct(product);
    const defaults = defaultConversionFor(product, { unit: item?.unit ?? "" });
    setConversionMode(defaults.conversionMode);
    setPackageAmount(defaults.packageAmount);
    setPackageUnit(defaults.packageUnit || "g");
    setUnitsPerSku(defaults.unitsPerSku);
  }

  async function submitVincularYAgregar() {
    if (!item || !selectedProduct) return;
    setError(null);
    try {
      await saveMappingMutation.mutateAsync({
        ingredientName: item.name,
        skuId: selectedProduct.skuId,
        sellerId: selectedProduct.sellerId,
        conversionMode,
        packageAmount: conversionMode === "measurable" ? Number(packageAmount) || null : null,
        packageUnit: conversionMode === "measurable" ? packageUnit || null : null,
        unitsPerSku: conversionMode === "grouped-units" ? Number(unitsPerSku) || null : null,
      });

      // If the user swapped to a different SKU and the old one was in this
      // week's cart, remove it before adding the new one — keeps the cart
      // aligned with the user's current preference, no orphan line.
      const skuChanged = !!(existingCartSkuId && existingCartSkuId !== selectedProduct.skuId);
      if (skuChanged && existingCartSkuId) {
        try {
          await removeCartMutation.mutateAsync({ weekId, skuId: existingCartSkuId });
        } catch (err) {
          console.warn("Failed to remove old SKU from cart during swap", err);
        }
      }

      await addToCartMutation.mutateAsync({ weekId, items: [toCartItem(item)] });
      onCartResult(
        skuChanged
          ? `${item.name} actualizado en el carrito.`
          : `${item.name} vinculado y agregado al carrito.`,
      );
      onCartChanged();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar la relación.");
    }
  }

  // Add the chosen product to the cart without saving a mapping. The user
  // gets the item this week without committing it as their long-term
  // preference for this ingredient.
  async function submitAgregarSinVincular() {
    if (!item || !selectedProduct) return;
    setError(null);
    try {
      const delta = computeQuantityForConversion({
        conversionMode,
        packageAmount: conversionMode === "measurable" ? Number(packageAmount) || null : null,
        packageUnit: conversionMode === "measurable" ? packageUnit || null : null,
        unitsPerSku: conversionMode === "grouped-units" ? Number(unitsPerSku) || null : null,
        measurementUnit: selectedProduct.measurementUnit,
        unitMultiplier: selectedProduct.unitMultiplier,
        totalGrams: item.totalGrams,
        totalUnits: item.totalUnits,
      });
      // Read live cart from React Query cache to compute the additive target.
      // setSkuQuantity is absolute, so we sum with whatever is already there.
      const cached = queryClient.getQueryData<StoreCart | null>(storeQueryKeys.cart(store, weekId));
      const currentQty =
        cached?.items.find((line) => line.skuId === selectedProduct.skuId)?.quantity ?? 0;
      await setSkuQuantityMutation.mutateAsync({
        weekId,
        skuId: selectedProduct.skuId,
        sellerId: selectedProduct.sellerId,
        quantity: currentQty + delta,
      });
      onCartResult(`${item.name} agregado al carrito (sin vincular).`);
      onCartChanged();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo agregar al carrito.");
    }
  }

  async function removeMapping() {
    if (!item) return;
    setError(null);
    try {
      await deleteMappingMutation.mutateAsync(item.name);
      onCartResult(`Vínculo eliminado: ${item.name}.`);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo eliminar la relación.");
    }
  }

  const saving =
    saveMappingMutation.isPending ||
    deleteMappingMutation.isPending ||
    addToCartMutation.isPending ||
    removeCartMutation.isPending ||
    setSkuQuantityMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Mobile-anchored bottom sheet, centered dialog on sm+. Slide-from-
          bottom on mobile (overridden via data-state utilities). Uses the
          regular Dialog primitive so the user can tap the overlay to dismiss. */}
      <DialogContent
        aria-label={`Vincular: ${item?.name ?? "ingrediente"}`}
        className={cn(
          "flex flex-col gap-0 p-0 max-h-[95vh] sm:max-h-[95vh]",
          "fixed inset-x-0 bottom-0 top-auto w-full max-w-none translate-x-0 translate-y-0 rounded-t-2xl border-t border-x border-b-0",
          "sm:bottom-auto sm:top-1/2 sm:left-1/2 sm:max-w-2xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:border-b",
          "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100",
          "sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0",
          "sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95",
        )}
      >
        <header className="flex shrink-0 flex-col gap-3 border-b bg-background px-5 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate text-base font-semibold">
                Vincular: {item?.name ?? "Ingrediente"}
              </DialogTitle>
              <DialogDescription className="truncate text-xs">
                {item ? formatShoppingAmount(item) : ""}
                {mapping ? ` · Actual: ${mapping.productName}` : ""}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-1">
              {mapping && (
                <button
                  type="button"
                  onClick={() => void removeMapping()}
                  disabled={saving}
                  aria-label="Quitar relación"
                  className={cn(
                    "grid size-9 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-40",
                    focusRing,
                  )}
                >
                  <Trash2Icon className="size-4" />
                </button>
              )}
              <DialogClose asChild>
                <button
                  type="button"
                  aria-label="Cerrar"
                  className={cn(
                    "grid size-10 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted",
                    focusRing,
                  )}
                >
                  <XIcon className="size-4" />
                </button>
              </DialogClose>
            </div>
          </div>
          {/* Search bar stays in the header — never scrolls away. */}
          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              void runSearch();
            }}
          >
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Buscar producto en ${storeLabel}`}
              className="h-9"
            />
            <Button type="submit" size="sm" disabled={loading}>
              <SearchIcon className="size-4" />
              Buscar
            </Button>
          </form>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

          {!mapping && otherStoreMapping && (
            <p className="rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
              En {STORE_META[otherStore(store)].label} lo tenías vinculado a{" "}
              <span className="font-medium text-foreground">{otherStoreMapping.productName}</span>.
              Elige un producto de tamaño parecido.
            </p>
          )}

          {/* Pre-skeleton: render 4 muted cards while the first search is in
              flight so "Sin resultados" never flashes before products load. */}
          {loading && products.length === 0 ? (
            <div className="grid gap-2 md:grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-24 rounded-md bg-muted animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid gap-2 md:grid-cols-2">
              {products.map((product) => {
                const active = selectedProduct?.skuId === product.skuId;
                return (
                  <button
                    type="button"
                    key={product.skuId}
                    onClick={() => selectProduct(product)}
                    className={cn(
                      "grid grid-cols-[72px_1fr] gap-3 rounded-md border p-2 text-left transition-colors",
                      active ? "border-primary bg-primary/5" : "hover:bg-muted/50",
                      focusRing,
                    )}
                  >
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt=""
                        className="size-[72px] rounded border object-contain"
                      />
                    ) : (
                      <span className="size-[72px] rounded border bg-muted" />
                    )}
                    <span className="min-w-0 space-y-1">
                      <span className="block text-sm font-medium">{product.productName}</span>
                      <span className="block text-xs text-muted-foreground">
                        {product.brand || product.sellerName || storeLabel} · SKU {product.skuId}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        S/ {product.price.toFixed(2)} · {product.measurementUnit} x {formatNumber(product.unitMultiplier)}
                        {product.packageAmount && product.packageUnit
                          ? ` · empaque ${formatNumber(product.packageAmount)} ${product.packageUnit}`
                          : ""}
                      </span>
                      <span className={`block text-xs ${product.isAvailable ? "text-emerald-700" : "text-muted-foreground"}`}>
                        {product.isAvailable ? `Disponible (${product.availableQuantity})` : "No disponible"}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {!loading && products.length === 0 && <p className="text-sm text-muted-foreground">Sin resultados.</p>}
        </div>

        {/* Sticky bottom: compact Porciones row + two action buttons. Stays in
            view so the user doesn't have to scroll past results to configure
            the conversion and submit. */}
        <div className="sticky bottom-0 z-10 border-t bg-background px-5 py-3 safe-area-bottom">
          {selectedProduct ? (
            <div className="space-y-2.5">
              <p className="truncate text-[12px] text-muted-foreground">
                <span className="font-medium text-foreground">{selectedProduct.productName}</span>
              </p>
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <Label htmlFor="conversion-mode" className="shrink-0 text-[11px] uppercase tracking-wide text-muted-foreground">
                    Porción
                  </Label>
                  <select
                    id="conversion-mode"
                    value={conversionMode}
                    onChange={(event) => setConversionMode(event.target.value as StoreMapping["conversionMode"])}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option value="measurable">Peso / volumen</option>
                    <option value="grouped-units">Unidades</option>
                  </select>
                </div>
                {conversionMode === "measurable" ? (
                  <>
                    <Input
                      id="package-amount"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={packageAmount}
                      onChange={(event) => setPackageAmount(event.target.value)}
                      aria-label="Cantidad por producto"
                      className="h-9 w-24"
                    />
                    <select
                      id="package-unit"
                      value={packageUnit}
                      onChange={(event) => setPackageUnit(event.target.value)}
                      aria-label="Unidad"
                      className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                    >
                      <option value="g">g</option>
                      <option value="kg">kg</option>
                      <option value="ml">ml</option>
                      <option value="l">l</option>
                    </select>
                  </>
                ) : (
                  <Input
                    id="units-per-sku"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={unitsPerSku}
                    onChange={(event) => setUnitsPerSku(event.target.value)}
                    aria-label="Unidades por producto"
                    className="h-9 w-24"
                  />
                )}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="h-11"
                  onClick={() => void submitAgregarSinVincular()}
                  disabled={!selectedProduct || saving}
                >
                  {saving ? <ProgressDots aria-label="Agregando" /> : "Agregar sin vincular"}
                </Button>
                <Button
                  type="button"
                  size="lg"
                  className="ml-auto h-11"
                  onClick={() => void submitVincularYAgregar()}
                  disabled={!selectedProduct || saving}
                >
                  {saving ? <ProgressDots aria-label="Guardando" /> : (
                    <>
                      <ShoppingCartIcon className="size-4" />
                      Vincular y agregar
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-center text-sm text-muted-foreground">
              Elige un producto para vincular o agregar.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main orchestrator ──

export function ShoppingList() {
  const { state } = useWeekPlan();
  const { friends } = useFriends();
  const { user, preferredStore: store } = useAuth();
  const { requestLogout, logoutDialog } = useLogoutWithConfirm();
  const storeMeta = STORE_META[store];
  const storeLabel = storeMeta.label;
  const isWong = store === "wong";
  // Which friends' meals join my list: remembered per viewer, across weeks.
  const [friendPrefs, setFriendPrefs] = useState<ComprasFriendPrefs>(() =>
    user ? loadComprasFriendPrefs(user.id) : {},
  );
  const [friendPlans, setFriendPlans] = useState<Map<number, WeekPlan>>(new Map());
  /** Included friends whose week failed to load this time (their last loaded copy, if any, stays). */
  const [friendLoadFailed, setFriendLoadFailed] = useState<number[]>([]);
  /** Bumped to re-read friends' weeks (coming back to the tab, Reintentar). */
  const [friendReloadTick, setFriendReloadTick] = useState(0);
  const [boughtItems, setBoughtItems] = useState<Set<string>>(() => loadBoughtItems(store, state.currentWeekId));

  // Store queries (scoped to the user's preferred supermarket)
  const statusQuery = useStoreStatus(store);
  const storeStatus = statusQuery.data ?? null;
  const storeConfigured = storeStatus?.configured !== false;
  const isConnected = !!storeStatus?.connected;
  const appAuthMissing = statusQuery.error instanceof Error && statusQuery.error.message === "Not authenticated";

  // Delivery location is the one per-store difference: PlazaVea uses a typed
  // address + coverage districts, Wong a saved address from the Wong account.
  const coverageQuery = usePlazaVeaCoverage(isConnected && !isWong);
  const plazaLocationQuery = usePlazaVeaLocation(isConnected && !isWong);
  const wongLocationQuery = useWongLocation(isConnected && isWong);
  const locationQuery = isWong ? wongLocationQuery : plazaLocationQuery;
  const mappingsQuery = useStoreMappings(store, isConnected);
  // Links in the other store are a reference when re-linking after a switch.
  const otherStoreMappingsQuery = useStoreMappings(otherStore(store), true);
  const cartQuery = useStoreCart(store, state.currentWeekId, isConnected);

  const coverageDistricts = coverageQuery.data?.districts ?? [];
  const coverageLoading = coverageQuery.isLoading;
  const plazaLocation = isWong ? null : plazaLocationQuery.data ?? null;
  const storeLocation: StoreDeliveryLocation | null = isWong
    ? wongLocationQuery.data?.location ?? null
    : plazaLocation;
  const wongAddresses = wongLocationQuery.data?.savedAddresses ?? [];
  const storeCart = cartQuery.data ?? null;
  const storeCartError = cartQuery.error instanceof Error ? cartQuery.error.message : null;

  // Links only drive the list while connected (same as before multi-store).
  // Gate explicitly: the other-store query above may have cached this store's
  // links even though this query is disabled.
  const storeMappings = useMemo(
    () => new Map(isConnected ? (mappingsQuery.data ?? []).map((m) => [m.ingredientName, m]) : []),
    [isConnected, mappingsQuery.data],
  );
  const otherStoreMappings = useMemo(
    () => new Map((otherStoreMappingsQuery.data ?? []).map((m) => [m.ingredientName, m])),
    [otherStoreMappingsQuery.data],
  );

  // Mutations
  const connectMutation = useConnectStore(store);
  const disconnectMutation = useDisconnectStore(store);
  const setLocationMutation = useSetPlazaVeaLocation();
  const setWongLocationMutation = useSetWongLocation();
  const addCartMutation = useAddStoreMappedCartItems(store);
  const removeCartMutation = useRemoveStoreCartItem(store);
  const setQuantityMutation = useSetStoreCartItemQuantity(store);
  const setSkuQuantityMutation = useSetStoreCartSkuQuantity(store);
  const [selectingWongAddressId, setSelectingWongAddressId] = useState<string | null>(null);
  const [geoResolving, setGeoResolving] = useState(false);
  const [quickAddingIngredient, setQuickAddingIngredient] = useState<string | null>(null);
  const [removingSkuId, setRemovingSkuId] = useState<string | null>(null);
  const [adjustingIngredient, setAdjustingIngredient] = useState<string | null>(null);
  const [pendingSkuQuantity, setPendingSkuQuantity] = useState<string | null>(null);
  const [bulkAdding, setBulkAdding] = useState(false);
  const [extrasDialogOpen, setExtrasDialogOpen] = useState(false);

  // Toast queue replaces the older inline plazaMessage / plazaError strings.
  const { toast, show: showToast, dismiss: dismissToast } = useComprasToast();

  // Sheet + connect form state
  const [sheetOpen, setSheetOpen] = useState(false);
  // Confirm-disconnect dialog lifted out of the sheet (M4) so the two dialogs
  // are siblings rather than nested.
  const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
  const [storeEmail, setStoreEmail] = useState("");
  const [storePassword, setStorePassword] = useState("");
  const [connectError, setConnectError] = useState<string | null>(null);

  // Address form state
  const [locationStreet, setLocationStreet] = useState("");
  const [locationNumber, setLocationNumber] = useState("");
  const [selectedDistrictUbigeo, setSelectedDistrictUbigeo] = useState("");
  const [districtQuery, setDistrictQuery] = useState("");
  const [districtAutocompleteOpen, setDistrictAutocompleteOpen] = useState(false);
  const [highlightedDistrictIndex, setHighlightedDistrictIndex] = useState<number | null>(null);
  const [locationLatitude, setLocationLatitude] = useState("");
  const [locationLongitude, setLocationLongitude] = useState("");
  const [locationError, setLocationError] = useState<string | null>(null);
  const [addressMode, setAddressMode] = useState<"view" | "edit">("edit");

  const [selectedPickerItem, setSelectedPickerItem] = useState<AggregatedItem | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Reload bought items when the week or store changes
  useEffect(() => {
    setBoughtItems(loadBoughtItems(store, state.currentWeekId));
  }, [store, state.currentWeekId]);

  // Keep the connect form's email field synced with the server-side status.
  useEffect(() => {
    if (!isConnected) return;
    setStoreEmail(storeStatus?.email ?? "");
  }, [isConnected, storeStatus?.email]);

  // Switching supermarkets: drop transient form/sheet state that belonged to
  // the previous store so nothing (e.g. a typed password) leaks across.
  useEffect(() => {
    setStorePassword("");
    setConnectError(null);
    setLocationError(null);
    setPickerOpen(false);
    setExtrasDialogOpen(false);
  }, [store]);

  // Sync address form inputs exactly once when the saved location first becomes
  // available. Subsequent refetches must NOT overwrite the user's in-progress
  // typing. After-save/cancel sync happens explicitly via the handlers below.
  const initialLocationSyncedRef = useRef(false);
  useEffect(() => {
    if (!isConnected || isWong) {
      initialLocationSyncedRef.current = false;
      return;
    }
    if (!plazaLocation || initialLocationSyncedRef.current) return;
    initialLocationSyncedRef.current = true;
    applyLocationToInputs(plazaLocation, coverageQuery.data?.districts ?? []);
    setAddressMode(plazaLocation.configured ? "view" : "edit");
  }, [isConnected, isWong, plazaLocation, coverageQuery.data]);

  function applyLocationToInputs(
    location: NonNullable<typeof plazaLocation>,
    districts: PlazaVeaCoverageDistrict[],
  ) {
    setLocationStreet(location.street ?? "");
    setLocationNumber(location.number ?? "");
    setSelectedDistrictUbigeo(location.postalCode ?? "");
    const selectedDistrict = districts.find((district) => district.ubigeo === location.postalCode);
    setDistrictQuery(selectedDistrict ? plazaDistrictLabel(selectedDistrict) : location.neighborhood ?? "");
    // Don't pre-fill the form with (0, 0) — that's the same bad value that
    // would silently pass the manual-save flow. Leaving the inputs blank lets
    // the save fall through to the district's warehouse coordinates.
    const validCoords =
      location.latitude !== null &&
      location.longitude !== null &&
      !(location.latitude === 0 && location.longitude === 0);
    if (validCoords) {
      setLocationLatitude(String(location.latitude));
      setLocationLongitude(String(location.longitude));
    } else {
      setLocationLatitude("");
      setLocationLongitude("");
    }
  }

  function startEditingAddress() {
    if (plazaLocation) applyLocationToInputs(plazaLocation, coverageDistricts);
    setLocationError(null);
    setAddressMode("edit");
  }

  function cancelEditingAddress() {
    if (plazaLocation) applyLocationToInputs(plazaLocation, coverageDistricts);
    setLocationError(null);
    setDistrictAutocompleteOpen(false);
    setHighlightedDistrictIndex(null);
    setAddressMode("view");
  }

  function markBought(itemName: string) {
    setBoughtItems((prev) => {
      if (prev.has(itemName)) return prev;
      const next = new Set(prev);
      next.add(itemName);
      saveBoughtItems(store, state.currentWeekId, next);
      return next;
    });
  }

  function toggleBought(itemName: string) {
    setBoughtItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemName)) {
        next.delete(itemName);
      } else {
        next.add(itemName);
      }
      saveBoughtItems(store, state.currentWeekId, next);
      return next;
    });
  }

  async function handleStoreConnect() {
    setConnectError(null);
    try {
      await connectMutation.mutateAsync({ email: storeEmail.trim(), password: storePassword });
      setStorePassword("");
      showToast({ message: `Conectado con ${storeLabel}.`, tone: "success" });
    } catch (err) {
      const message = err instanceof Error ? err.message : `No se pudo conectar ${storeLabel}.`;
      setConnectError(message);
    }
  }

  async function handleStoreDisconnect() {
    try {
      await disconnectMutation.mutateAsync();
      setLocationStreet("");
      setLocationNumber("");
      setSelectedDistrictUbigeo("");
      setDistrictQuery("");
      setDistrictAutocompleteOpen(false);
      setHighlightedDistrictIndex(null);
      setLocationLatitude("");
      setLocationLongitude("");
      setLocationError(null);
      setAddressMode("edit");
      // Close the confirm first, then the sheet, then surface the success.
      setConfirmDisconnectOpen(false);
      setSheetOpen(false);
      showToast({ message: `Cuenta de ${storeLabel} desconectada.`, tone: "success" });
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : `No se pudo desconectar ${storeLabel}.`,
        tone: "error",
      });
    }
  }

  function openPicker(item: AggregatedItem) {
    if (!storeLocationConfigured) {
      showToast({
        message: isWong
          ? "Elige tu dirección de Wong antes de buscar productos."
          : "Configura tu dirección antes de buscar productos.",
        tone: "error",
        actionLabel: "Abrir ajustes",
        onAction: () => setSheetOpen(true),
      });
      return;
    }
    setSelectedPickerItem(item);
    setPickerOpen(true);
  }

  async function selectWongAddress(addressId: string) {
    if (selectingWongAddressId) return;
    setLocationError(null);
    setSelectingWongAddressId(addressId);
    try {
      const result = await setWongLocationMutation.mutateAsync(addressId);
      showToast({
        message: `Entregas en ${result.location.neighborhood ?? "tu dirección"}.`,
        tone: "success",
      });
    } catch (err) {
      setLocationError(err instanceof Error ? err.message : "No se pudo guardar la dirección.");
    } finally {
      setSelectingWongAddressId(null);
    }
  }

  function selectCoverageDistrict(district: PlazaVeaCoverageDistrict) {
    setSelectedDistrictUbigeo(district.ubigeo);
    setDistrictQuery(plazaDistrictLabel(district));
    setDistrictAutocompleteOpen(false);
    setHighlightedDistrictIndex(null);
  }

  function handleDistrictQueryChange(value: string) {
    setDistrictQuery(value);
    setDistrictAutocompleteOpen(true);
    setHighlightedDistrictIndex(0);
    const exact = coverageDistricts.find((district) => normalizeSearch(plazaDistrictLabel(district)) === normalizeSearch(value));
    setSelectedDistrictUbigeo(exact?.ubigeo ?? "");
  }

  function focusDistrictAutocomplete() {
    setDistrictAutocompleteOpen(true);
    setHighlightedDistrictIndex(null);
  }

  function blurDistrictAutocomplete() {
    window.setTimeout(() => {
      setDistrictAutocompleteOpen(false);
      setHighlightedDistrictIndex(null);
    }, 120);
  }

  function handleDistrictKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      if (filteredCoverageDistricts.length === 0) return;
      event.preventDefault();
      setDistrictAutocompleteOpen(true);
      setHighlightedDistrictIndex((current) => {
        if (current === null) return filteredCoverageDistricts.length > 1 ? 1 : 0;
        return (current + 1) % filteredCoverageDistricts.length;
      });
      return;
    }

    if (event.key === "ArrowUp") {
      if (filteredCoverageDistricts.length === 0) return;
      event.preventDefault();
      setDistrictAutocompleteOpen(true);
      setHighlightedDistrictIndex((current) => {
        if (current === null) return filteredCoverageDistricts.length - 1;
        return (current - 1 + filteredCoverageDistricts.length) % filteredCoverageDistricts.length;
      });
      return;
    }

    if (event.key === "Enter") {
      if (!districtAutocompleteOpen || filteredCoverageDistricts.length === 0) return;
      event.preventDefault();
      selectCoverageDistrict(filteredCoverageDistricts[highlightedDistrictIndex ?? 0]!);
      return;
    }

    if (event.key === "Escape") {
      setDistrictAutocompleteOpen(false);
      setHighlightedDistrictIndex(null);
    }
  }

  async function savePlazaLocation(
    latitude: number | null,
    longitude: number | null,
    district: PlazaVeaCoverageDistrict,
  ) {
    if (!locationStreet.trim() || !locationNumber.trim()) {
      setLocationError("Completa dirección y número.");
      return;
    }
    setLocationError(null);
    try {
      await setLocationMutation.mutateAsync({
        latitude,
        longitude,
        street: locationStreet.trim(),
        number: locationNumber.trim(),
        neighborhood: district.displayName,
        postalCode: district.ubigeo,
        city: district.provinceName ?? "Lima",
        state: district.departmentName ?? "Lima",
      });
      setAddressMode("view");
      showToast({ message: "Dirección guardada.", tone: "success" });
    } catch (err) {
      setLocationError(err instanceof Error ? err.message : "No se pudo guardar la dirección.");
    }
  }

  async function saveManualPlazaLocation() {
    const district = coverageDistricts.find((candidate) => candidate.ubigeo === selectedDistrictUbigeo);
    if (!district) {
      setLocationError(`Selecciona un distrito con cobertura ${storeLabel}.`);
      return;
    }
    // Pass user-blessed coords if "Usar mi ubicación" was used; otherwise let
    // the server forward-geocode the typed address. The previous fallback to
    // `firstStoreCoordinates(district)` produced a Plaza Vea STORE address —
    // VTEX refuses to deliver to those, so the whole cart ended up as
    // "agotado" at checkout.
    const coordinates = parseUserCoordinates(locationLatitude, locationLongitude);
    await savePlazaLocation(coordinates?.latitude ?? null, coordinates?.longitude ?? null, district);
  }

  async function useCurrentBrowserLocation() {
    if (!navigator.geolocation) {
      setLocationError("Este navegador no permite obtener ubicación.");
      return;
    }
    setGeoResolving(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        void (async () => {
          try {
            const latitude = position.coords.latitude;
            const longitude = position.coords.longitude;
            const resolved = await reverseGeocodePlazaVeaLocation(latitude, longitude);
            const district = findDistrictByName(coverageDistricts, resolved.district);
            setLocationLatitude(String(latitude));
            setLocationLongitude(String(longitude));
            if (resolved.street) setLocationStreet(resolved.street);
            if (resolved.number) setLocationNumber(resolved.number);
            if (district) {
              selectCoverageDistrict(district);
            } else if (resolved.district) {
              setDistrictQuery(resolved.district);
              setSelectedDistrictUbigeo("");
            }
            const missing = [
              resolved.street ? null : "calle",
              resolved.number ? null : "número",
              district ? null : "distrito",
            ].filter(Boolean) as string[];
            if (missing.length > 0) {
              setLocationError(`Encontramos parte de tu ubicación. Completa: ${missing.join(", ")}.`);
            } else {
              setLocationError(null);
              showToast({ message: "Ubicación detectada. Revisa y guarda.", tone: "info" });
            }
          } catch (err) {
            setLocationError(err instanceof Error ? err.message : "No pudimos resolver la ubicación.");
          } finally {
            setGeoResolving(false);
          }
        })();
      },
      (error) => {
        setGeoResolving(false);
        setLocationError(error.message || "No pudimos obtener tu ubicación.");
      },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  }

  async function quickAddIngredientToCart(item: AggregatedItem) {
    setQuickAddingIngredient(item.name);
    try {
      await addCartMutation.mutateAsync({ weekId: state.currentWeekId, items: [toCartItem(item)] });
      markBought(item.name);
      showToast({ message: `Agregado: ${item.name}.`, tone: "success" });
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : "No se pudo agregar al carrito.",
        tone: "error",
      });
    } finally {
      setQuickAddingIngredient(null);
    }
  }

  async function removeIngredientFromCart(skuId: string, sellerId: string | null, label: string) {
    setRemovingSkuId(skuId);
    try {
      await removeCartMutation.mutateAsync({
        weekId: state.currentWeekId,
        skuId,
        sellerId: sellerId ?? undefined,
      });
      showToast({ message: `${label} quitado del carrito.`, tone: "success" });
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : "No se pudo quitar del carrito.",
        tone: "error",
      });
    } finally {
      setRemovingSkuId(null);
    }
  }

  async function completeIncompleteCart() {
    if (incompleteLinkedItems.length === 0) return;
    setBulkAdding(true);
    let completed = 0;
    let failed = 0;
    for (const item of incompleteLinkedItems) {
      try {
        await setQuantityMutation.mutateAsync({
          weekId: state.currentWeekId,
          ingredientName: item.name,
          totalGrams: item.totalGrams,
          totalUnits: item.totalUnits,
        });
        markBought(item.name);
        completed += 1;
      } catch (err) {
        failed += 1;
        console.warn(`Failed to set quantity for ${item.name}`, err);
      }
    }
    setBulkAdding(false);
    if (failed === 0) {
      showToast({ message: `${completed} productos agregados al carrito.`, tone: "success" });
    } else {
      showToast({
        message: `Se agregaron ${completed}; fallaron ${failed}.`,
        tone: "error",
      });
    }
  }

  // Quick-add (+1) for a non-ingredient SKU like the plastic-bag charge.
  // Reads the current cart from React Query cache so taps stack additively
  // (set-sku-quantity is absolute, so we sum locally first).
  async function bumpExtraSku(skuId: string, sellerId: string, label: string) {
    setPendingSkuQuantity(skuId);
    try {
      const current = cartItemsBySku.get(skuId)?.quantity ?? 0;
      await setSkuQuantityMutation.mutateAsync({
        weekId: state.currentWeekId,
        skuId,
        sellerId,
        quantity: current + 1,
      });
      showToast({ message: `${label} agregado al carrito.`, tone: "success" });
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : "No se pudo agregar al carrito.",
        tone: "error",
      });
    } finally {
      setPendingSkuQuantity(null);
    }
  }

  async function changeCartSkuQuantity(
    skuId: string,
    sellerId: string | null,
    newQuantity: number,
  ) {
    if (newQuantity < 0) return;
    setPendingSkuQuantity(skuId);
    try {
      await setSkuQuantityMutation.mutateAsync({
        weekId: state.currentWeekId,
        skuId,
        sellerId: sellerId ?? undefined,
        quantity: newQuantity,
      });
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : "No se pudo actualizar la cantidad.",
        tone: "error",
      });
    } finally {
      setPendingSkuQuantity(null);
    }
  }

  async function adjustCartQuantityForIngredient(item: AggregatedItem) {
    setAdjustingIngredient(item.name);
    try {
      await setQuantityMutation.mutateAsync({
        weekId: state.currentWeekId,
        ingredientName: item.name,
        totalGrams: item.totalGrams,
        totalUnits: item.totalUnits,
      });
      markBought(item.name);
      showToast({ message: `Ajustado: ${item.name}.`, tone: "success" });
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : "No se pudo ajustar la cantidad.",
        tone: "error",
      });
    } finally {
      setAdjustingIngredient(null);
    }
  }

  // Friends list filtered to accepted only.
  const acceptedFriends = useMemo(
    () => friends.filter((f) => f.status === "accepted"),
    [friends],
  );
  const includedFriends = useMemo(
    () => includedFriendIds(friendPrefs, acceptedFriends.map((f) => f.friend_id)),
    [friendPrefs, acceptedFriends],
  );

  // A stable key: `includedFriends` is a new Set whenever the friends list refreshes.
  const includedKey = [...includedFriends].sort((a, b) => a - b).join(",");

  useEffect(() => {
    const onBack = () => {
      if (document.visibilityState === "visible") setFriendReloadTick((t) => t + 1);
    };
    document.addEventListener("visibilitychange", onBack);
    window.addEventListener("focus", onBack);
    return () => {
      document.removeEventListener("visibilitychange", onBack);
      window.removeEventListener("focus", onBack);
    };
  }, []);

  // Included friends' weeks, read fresh on every visit and on coming back to
  // the tab: I may have just planned them (friend mode saves on the way out,
  // so wait for pending saves first), and they may have changed them since.
  useEffect(() => {
    const weekId = state.currentWeekId;
    const ids = includedKey ? includedKey.split(",").map(Number) : [];
    if (!weekId || ids.length === 0) return;
    let cancelled = false;
    void (async () => {
      await flushPendingSaves().catch((error: unknown) =>
        console.error("Failed to save before reading friends' weeks", error),
      );
      const results = await Promise.allSettled(
        ids.map((friendId) => fetchWeekSnapshot({ kind: "friend", friendId }, weekId)),
      );
      if (cancelled) return;
      const failed: number[] = [];
      const loaded = new Map<number, WeekPlan | null>();
      results.forEach((result, i) => {
        const friendId = ids[i]!;
        if (result.status === "fulfilled") {
          loaded.set(friendId, result.value.plan ? migrateStoredWeek(result.value.plan) : null);
        } else {
          console.error(`Failed to load friend ${friendId}'s week`, result.reason);
          failed.push(friendId);
        }
      });
      setFriendPlans((prev) => {
        const next = new Map(prev);
        for (const [friendId, plan] of loaded) {
          if (plan) next.set(friendId, plan);
          else next.delete(friendId);
        }
        return next;
      });
      setFriendLoadFailed(failed);
    })();
    return () => {
      cancelled = true;
    };
  }, [includedKey, state.currentWeekId, friendReloadTick]);

  // Reset friend plans when week changes
  useEffect(() => {
    setFriendPlans(new Map());
    setFriendLoadFailed([]);
  }, [state.currentWeekId]);

  const failedFriendNames = acceptedFriends
    .filter((f) => includedFriends.has(f.friend_id) && friendLoadFailed.includes(f.friend_id))
    .map((f) => f.username);

  function toggleFriend(friendId: number) {
    const next = setIncluded(friendPrefs, friendId, !includedFriends.has(friendId));
    setFriendPrefs(next);
    if (user) saveComprasFriendPrefs(user.id, next);
  }

  const myItems = useMemo(
    () => aggregateWeek(state.weekPlan),
    [state.weekPlan],
  );

  const items = useMemo(() => {
    if (includedFriends.size === 0) return myItems;
    const allItemArrays = [myItems];
    for (const friendId of includedFriends) {
      const friendPlan = friendPlans.get(friendId);
      if (!friendPlan) continue;
      allItemArrays.push(aggregateWeek(friendPlan));
    }
    return mergeAggregatedItems(...allItemArrays);
  }, [myItems, includedFriends, friendPlans]);

  const storeLocationConfigured = Boolean(storeLocation?.configured);

  // Only SKUs of mappings whose ingredient is actually in this week's items.
  const mappedSkuIdsForCurrentWeek = useMemo(() => {
    const set = new Set<string>();
    for (const item of items) {
      const mapping = storeMappings.get(item.name);
      if (mapping) set.add(mapping.skuId);
    }
    return set;
  }, [items, storeMappings]);

  const cartItemsBySku = useMemo(() => {
    const map = new Map<string, StoreCartProduct>();
    for (const product of storeCart?.items ?? []) {
      if (!map.has(product.skuId)) {
        map.set(product.skuId, product);
      }
    }
    return map;
  }, [storeCart]);

  const unmatchedCartItems = useMemo(
    () => (storeCart?.items ?? []).filter((product) => !mappedSkuIdsForCurrentWeek.has(product.skuId)),
    [storeCart, mappedSkuIdsForCurrentWeek],
  );

  // Items in this week that have a saved mapping but either aren't in the
  // store cart at all, or are there with an insufficient quantity.
  const incompleteLinkedItems = useMemo(() => {
    const list: AggregatedItem[] = [];
    for (const item of items) {
      const mapping = storeMappings.get(item.name);
      if (!mapping) continue;
      const cartItem = cartItemsBySku.get(mapping.skuId) ?? null;
      if (!cartItem) {
        list.push(item);
        continue;
      }
      if (computeCartSufficiency(item, mapping, cartItem.quantity).status === "insufficient") {
        list.push(item);
      }
    }
    return list;
  }, [items, storeMappings, cartItemsBySku]);

  const linkedItemCount = useMemo(
    () => items.filter((it) => storeMappings.has(it.name)).length,
    [items, storeMappings],
  );

  // Ingredients without a saved product in this store. Bought = handled elsewhere.
  const unlinkedItems = useMemo(
    () => items.filter((it) => !storeMappings.has(it.name) && !boughtItems.has(it.name)),
    [items, storeMappings, boughtItems],
  );

  const cartIsReady =
    linkedItemCount > 0 &&
    incompleteLinkedItems.length === 0 &&
    unlinkedItems.length === 0 &&
    (storeCart?.items.length ?? 0) > 0;

  const filteredCoverageDistricts = useMemo(
    () => coverageDistricts.filter((district) => districtMatchesQuery(district, districtQuery)).slice(0, 12),
    [coverageDistricts, districtQuery],
  );
  const activeDistrictOptionId = districtAutocompleteOpen &&
    highlightedDistrictIndex !== null &&
    filteredCoverageDistricts[highlightedDistrictIndex]
    ? `plazavea-location-district-option-${filteredCoverageDistricts[highlightedDistrictIndex].ubigeo}`
    : undefined;

  // Ready count = ingredients the user has explicitly marked as taken care of
  // (the checkbox). Could mean "added to cart" or "I'll source this elsewhere";
  // the user controls what "listo" means.
  const readyCount = useMemo(
    () => items.filter((item) => boughtItems.has(item.name)).length,
    [items, boughtItems],
  );

  // Total ingredients with no saved mapping in this store, regardless of the
  // bought-checkbox state. Surfaces the persistent "vincular pending" debt
  // even after a user checks an ingredient manually.
  const unlinkedItemsAllStates = useMemo(
    () => items.filter((it) => !storeMappings.has(it.name)),
    [items, storeMappings],
  );

  // Connection state for the sheet + context strip
  const connectionState: SheetConnectionState = (() => {
    if (appAuthMissing) return "app-auth-missing";
    if (!storeConfigured) return "configured-missing";
    if (!isConnected) return "disconnected";
    return storeLocationConfigured ? "connected-with-address" : "connected-no-address";
  })();

  const contextStrip = (() => {
    const map: Record<SheetConnectionState, { title: string; secondary: string | null; tone: StoreConnectionTone; disabled?: boolean }> = {
      "app-auth-missing": { title: "Sesión expirada", secondary: "Vuelve a iniciar sesión", tone: "danger" },
      "configured-missing": { title: "No disponible", secondary: null, tone: "muted", disabled: true },
      "disconnected": { title: "Conectar tu cuenta", secondary: "Para armar el carrito automáticamente", tone: "neutral" },
      "connected-no-address": {
        title: isWong ? "Elige tu dirección" : "Configura tu dirección",
        secondary: `${storeLabel} necesita saber dónde entregar`,
        tone: "warning",
      },
      "connected-with-address": {
        title: storeLocation?.neighborhood ?? "Dirección configurada",
        secondary: storeStatus?.email ?? null,
        tone: "ready",
      },
    };
    return map[connectionState];
  })();

  // Auto-open the sheet only when the connection *transitions into*
  // `connected-no-address`, not whenever the static state happens to be there.
  // Without this guard, the brief flicker back into `connected-no-address`
  // during the post-save refetch race would re-open the sheet right after the
  // user just saved their address.
  const lastConnectionStateRef = useRef<SheetConnectionState | null>(null);
  const autoOpenedForUserRef = useRef<string | null>(null);
  // The gate is per user *and* store: switching to a store that still needs
  // an address should prompt once for that store too.
  const autoOpenKey = `${user?.id ?? "anon"}:${store}`;

  // Reset the "already auto-opened" gate and transition tracking on user or
  // store change.
  useEffect(() => {
    autoOpenedForUserRef.current = null;
    lastConnectionStateRef.current = null;
  }, [autoOpenKey]);

  useEffect(() => {
    // Don't record transitions while the location is loading: "no address
    // yet" during a fetch is not an answer, and consuming the transition here
    // would make the post-load check see no transition and never auto-open.
    if (locationQuery.isFetching) return;
    const previous = lastConnectionStateRef.current;
    const justConnected = previous === "disconnected" && connectionState === "connected-no-address";
    const firstPaintAfterReload =
      previous === null &&
      connectionState === "connected-no-address" &&
      autoOpenedForUserRef.current !== autoOpenKey;

    if (
      connectionState === "connected-no-address" &&
      (justConnected || firstPaintAfterReload) &&
      autoOpenedForUserRef.current !== autoOpenKey
    ) {
      autoOpenedForUserRef.current = autoOpenKey;
      const t = setTimeout(() => setSheetOpen(true), 400);
      lastConnectionStateRef.current = connectionState;
      return () => clearTimeout(t);
    }

    lastConnectionStateRef.current = connectionState;
  }, [connectionState, locationQuery.isFetching, autoOpenKey]);

  // ── Bulk action selection for the ActionBar ──
  const actionBarVariant: ActionBarVariant = (() => {
    if (!isConnected || !storeLocationConfigured || items.length === 0) return "idle";
    if (cartIsReady && storeCart?.orderFormId) return "ready";
    if (incompleteLinkedItems.length > 0) return "add-missing";
    if (linkedItemCount === 0 && unlinkedItems.length > 0) return "link-needed";
    if (unlinkedItems.length > 0) return "unlinked-only";
    return "idle";
  })();

  // Ingredient list refs for scroll-to-row on "Vincular productos" CTA.
  const rowRefs = useRef<Map<string, HTMLLIElement | null>>(new Map());

  function scrollToAndPickFirstUnlinked() {
    const first = unlinkedItems[0];
    if (!first) return;
    const node = rowRefs.current.get(first.name);
    if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => openPicker(first), 250);
  }

  function openCheckout() {
    if (!storeCart?.orderFormId) return;
    window.open(storeMeta.checkoutUrl(storeCart.orderFormId), "_blank", "noopener,noreferrer");
  }

  function handleActionBarPrimary() {
    switch (actionBarVariant) {
      case "link-needed":
        scrollToAndPickFirstUnlinked();
        return;
      case "add-missing":
        void completeIncompleteCart();
        return;
      case "ready":
      case "unlinked-only":
        openCheckout();
        return;
      default:
        return;
    }
  }

  // (v2 removed the inline "Ir a <store>" secondary link from the ActionBar.
  // In `unlinked-only` the primary outline-variant CTA is the escape hatch.)

  // Group items by category for rendering
  const grouped = useMemo(() => {
    const map = new Map<string, AggregatedItem[]>();
    for (const item of items) {
      const list = map.get(item.category) ?? [];
      list.push(item);
      map.set(item.category, list);
    }
    return Array.from(map.entries()).sort((a, b) =>
      (CATEGORY_ORDER[a[0]] ?? 99) - (CATEGORY_ORDER[b[0]] ?? 99),
    );
  }, [items]);

  // ProgressMeter visibility + tone derivation
  const meterTone: ProgressMeterTone = (() => {
    if (items.length > 0 && readyCount === items.length) return "ready";
    if (unlinkedItemsAllStates.length > 0) return "info";
    return "neutral";
  })();
  // The label always tracks vincular debt — count of ingredients without a
  // saved mapping in this store, regardless of the bought checkbox.
  const meterStatusLabel: string =
    unlinkedItemsAllStates.length > 0
      ? `${unlinkedItemsAllStates.length} sin vincular`
      : items.length > 0 && readyCount === items.length
        ? "Todo listo"
        : "Todo vinculado";

  // Surface cart-fetch error as a toast (replaces inline destructive text).
  const lastReportedCartError = useRef<string | null>(null);
  useEffect(() => {
    if (storeCartError && storeCartError !== lastReportedCartError.current) {
      lastReportedCartError.current = storeCartError;
      showToast({
        message: `No pudimos cargar el carrito de ${storeLabel}.`,
        tone: "error",
        actionLabel: "Reintentar",
        onAction: () => {
          void cartQuery.refetch();
        },
      });
    }
    if (!storeCartError) {
      lastReportedCartError.current = null;
    }
  }, [storeCartError, cartQuery, showToast]);

  // Secondary line text for the ActionBar. Spanish singular/plural agreement
  // matters here — keep the copy correct when counts hit 1.
  function actionBarSecondaryLine(): string {
    if (actionBarVariant === "link-needed") {
      return unlinkedItems.length === 1
        ? "Vincula 1 ingrediente para empezar"
        : `Vincula los ${unlinkedItems.length} ingredientes para empezar`;
    }
    if (actionBarVariant === "add-missing") {
      const readyWord = readyCount === 1 ? "listo" : "listos";
      return `${readyCount} ${readyWord} · ${incompleteLinkedItems.length} por agregar`;
    }
    if (actionBarVariant === "unlinked-only") {
      return unlinkedItems.length === 1
        ? "Aún falta 1 ingrediente por vincular"
        : `Aún faltan ${unlinkedItems.length} ingredientes por vincular`;
    }
    if (actionBarVariant === "ready") {
      const count = storeCart?.itemCount ?? 0;
      const word = count === 1 ? "producto" : "productos";
      return `${count} ${word} · Carrito listo`;
    }
    return "";
  }

  // Wrap a section in the standard category-group container.
  function renderGroup(category: string, label: string, content: ReactNode, count: number) {
    return (
      <section key={category} aria-labelledby={`group-${category}`} className="space-y-2">
        <header className="flex items-baseline justify-between px-1">
          <h2
            id={`group-${category}`}
            className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
          >
            {label}
          </h2>
          <span className="text-[11px] tabular-nums text-muted-foreground">{count}</span>
        </header>
        <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
          {content}
        </ul>
      </section>
    );
  }

  const currentUserName = user?.username ?? "Tú";

  return (
    // Single flex column that always fills the scroll viewport. This keeps the
    // sticky-bottom ActionBar pinned to the bottom edge even when the
    // ingredient list is shorter than the viewport (M2 fix). The parent
    // <WeekLayout /> wrapper is `min-h-full flex flex-col`, so making this
    // child `flex-1` lets it grow to the full viewport height.
    <div className="flex flex-1 flex-col">
      {/* `pb-6` keeps the last row breathable above the sticky ComprasActionBar. */}
      <div className="flex-1 space-y-4 pb-6">
      <StoreContextStrip
        storeLabel={storeLabel}
        title={contextStrip.title}
        secondary={contextStrip.secondary}
        tone={contextStrip.tone}
        disabled={contextStrip.disabled}
        onOpen={() => setSheetOpen(true)}
        friends={acceptedFriends}
        includedFriendIds={includedFriends}
        onToggleFriend={toggleFriend}
        currentUserName={currentUserName}
      />
      {failedFriendNames.length > 0 && (
        <p role="alert" className="px-1 text-sm text-muted-foreground">
          No se pudo cargar la semana de {failedFriendNames.join(" y ")}.{" "}
          <button
            type="button"
            onClick={() => setFriendReloadTick((t) => t + 1)}
            className={cn("-my-2 inline-flex min-h-11 items-center rounded-md font-medium text-foreground underline underline-offset-2", focusRing)}
          >
            Reintentar
          </button>
        </p>
      )}

      {/* Progress meter — only when connected, located and there's anything to track */}
      {isConnected && storeLocationConfigured && items.length > 0 ? (
        <ProgressMeter
          readyCount={readyCount}
          totalCount={items.length}
          statusLabel={meterStatusLabel}
          tone={meterTone}
        />
      ) : (
        items.length > 0 && (
          <p className="px-1 pt-2 text-xs text-muted-foreground">
            {items.length} ingrediente{items.length === 1 ? "" : "s"} para esta semana
          </p>
        )
      )}

      {/* Ingredient groups */}
      {items.length === 0 ? (
        <div className="mx-auto max-w-md py-16 text-center">
          <ListXIcon className="mx-auto mb-3 size-10 text-muted-foreground/40" />
          <p className="text-base font-medium text-muted-foreground">
            Esta semana no tiene ingredientes.
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Arma tu plan en la pestaña <strong className="font-semibold text-foreground">Comidas</strong> y vuelve aquí.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(([category, categoryItems]) =>
            renderGroup(
              category,
              CATEGORY_LABELS[category] ?? category,
              categoryItems.map((item) => {
                const isBought = boughtItems.has(item.name);
                const mapping = storeMappings.get(item.name) ?? null;
                const cartMatch = mapping ? cartItemsBySku.get(mapping.skuId) ?? null : null;
                const sufficiency = mapping && cartMatch
                  ? computeCartSufficiency(item, mapping, cartMatch.quantity)
                  : null;
                return (
                  <IngredientRow
                    key={item.name}
                    rowRef={(node) => { rowRefs.current.set(item.name, node); }}
                    item={item}
                    amountLabel={formatShoppingAmount(item)}
                    isBought={isBought}
                    isConnected={isConnected}
                    isLocationConfigured={storeLocationConfigured}
                    mapping={mapping}
                    cartMatch={cartMatch}
                    sufficiency={sufficiency}
                    pending={{
                      quickAdd: quickAddingIngredient === item.name,
                      adjust: adjustingIngredient === item.name,
                      qtyUpdate: cartMatch ? pendingSkuQuantity === cartMatch.skuId : false,
                      remove: cartMatch ? removingSkuId === cartMatch.skuId : false,
                    }}
                    onToggleBought={() => toggleBought(item.name)}
                    onOpenPicker={() => openPicker(item)}
                    onQuickAdd={() => void quickAddIngredientToCart(item)}
                    onAdjust={() => void adjustCartQuantityForIngredient(item)}
                    onIncrement={() => {
                      if (cartMatch) void changeCartSkuQuantity(cartMatch.skuId, cartMatch.seller, cartMatch.quantity + 1);
                    }}
                    onDecrement={() => {
                      if (cartMatch) void changeCartSkuQuantity(cartMatch.skuId, cartMatch.seller, cartMatch.quantity - 1);
                    }}
                    onSetQuantity={(next) => {
                      if (cartMatch) void changeCartSkuQuantity(cartMatch.skuId, cartMatch.seller, next);
                    }}
                    onRemove={() => {
                      if (cartMatch) void removeIngredientFromCart(cartMatch.skuId, cartMatch.seller, cartMatch.name);
                    }}
                    onOpenAddressSheet={() => setSheetOpen(true)}
                  />
                );
              }),
              categoryItems.length,
            ),
          )}

          {/* Extras del carrito group + entry point */}
          {isConnected && (
            unmatchedCartItems.length > 0 ? (
              renderGroup(
                "extras",
                "Extras del carrito",
                <>
                  {unmatchedCartItems.map((product) => (
                    <li key={`${product.skuId}-${product.seller ?? ""}`}>
                      <CartLineCompact
                        product={product}
                        tone="neutral"
                        pending={pendingSkuQuantity === product.skuId || removingSkuId === product.skuId}
                        onIncrement={() => void changeCartSkuQuantity(product.skuId, product.seller, product.quantity + 1)}
                        onDecrement={() => void changeCartSkuQuantity(product.skuId, product.seller, product.quantity - 1)}
                        onSetQuantity={(next) => void changeCartSkuQuantity(product.skuId, product.seller, next)}
                        onRemove={() => void removeIngredientFromCart(product.skuId, product.seller, product.name)}
                      />
                    </li>
                  ))}
                  <li>
                    <div className="flex w-full divide-x">
                      <button
                        type="button"
                        onClick={() => setExtrasDialogOpen(true)}
                        disabled={!storeLocationConfigured}
                        className={cn(
                          "flex flex-1 items-center justify-center gap-2 px-3 py-3 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground disabled:opacity-40",
                          focusRing,
                        )}
                      >
                        <PlusIcon className="size-4" />
                        Agregar producto extra
                      </button>
                      {storeMeta.extraSkus.map((extra) => (
                        <button
                          key={extra.skuId}
                          type="button"
                          onClick={() => void bumpExtraSku(extra.skuId, extra.sellerId, extra.label)}
                          disabled={!storeLocationConfigured || pendingSkuQuantity === extra.skuId}
                          className={cn(
                            "flex items-center justify-center gap-2 px-4 py-3 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground disabled:opacity-40",
                            focusRing,
                          )}
                        >
                          <PlusIcon className="size-4" />
                          {extra.label}
                        </button>
                      ))}
                    </div>
                  </li>
                </>,
                unmatchedCartItems.length,
              )
            ) : (
              storeLocationConfigured && (
                <div className="flex flex-wrap items-center gap-3 px-1 pt-1">
                  <button
                    type="button"
                    onClick={() => setExtrasDialogOpen(true)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-sm text-[12px] text-muted-foreground hover:text-foreground",
                      focusRing,
                    )}
                  >
                    <PlusIcon className="size-3.5" /> Agregar producto extra
                  </button>
                  {storeMeta.extraSkus.map((extra) => (
                    <button
                      key={extra.skuId}
                      type="button"
                      onClick={() => void bumpExtraSku(extra.skuId, extra.sellerId, extra.label)}
                      disabled={pendingSkuQuantity === extra.skuId}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-sm text-[12px] text-muted-foreground hover:text-foreground disabled:opacity-40",
                        focusRing,
                      )}
                    >
                      <PlusIcon className="size-3.5" /> {extra.label}
                    </button>
                  ))}
                </div>
              )
            )
          )}
        </div>
      )}

      </div>

      {/* Sticky-bottom ActionBar — lives at the end of the flex column so it
          stays glued to the bottom edge of the scroll viewport. */}
      <ComprasActionBar
        variant={actionBarVariant}
        totalCents={storeCart?.value ?? 0}
        itemCount={storeCart?.itemCount ?? 0}
        secondaryLine={actionBarSecondaryLine()}
        storeLabel={storeLabel}
        missingCount={incompleteLinkedItems.length}
        pending={bulkAdding || setQuantityMutation.isPending}
        onPrimary={handleActionBarPrimary}
        hideOnDesktop={false}
      />

      <StoreSheet
        store={store}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        connectionState={connectionState}
        status={storeStatus}
        onAppLogout={requestLogout}
        connectEmail={storeEmail}
        connectPassword={storePassword}
        onConnectEmailChange={setStoreEmail}
        onConnectPasswordChange={setStorePassword}
        onConnectSubmit={handleStoreConnect}
        connectPending={connectMutation.isPending}
        connectError={connectError}
        onRequestDisconnect={() => setConfirmDisconnectOpen(true)}
        disconnectPending={disconnectMutation.isPending}
        addressLoading={locationQuery.isLoading || (isConnected && !storeLocation && !locationQuery.isError)}
        addressError={locationQuery.isError ? (locationQuery.error instanceof Error ? locationQuery.error.message : "No pudimos cargar la dirección.") : null}
        onRetryAddress={() => { void locationQuery.refetch(); }}
        addressMode={addressMode}
        onStartEditingAddress={startEditingAddress}
        onCancelEditingAddress={cancelEditingAddress}
        location={storeLocation}
        wongAddresses={wongAddresses}
        selectedWongAddressId={isWong ? wongLocationQuery.data?.location.addressId ?? null : null}
        selectingWongAddressId={selectingWongAddressId}
        onSelectWongAddress={(addressId) => void selectWongAddress(addressId)}
        onRefreshWongAddresses={() => { void wongLocationQuery.refetch(); }}
        refreshingWongAddresses={wongLocationQuery.isFetching}
        street={locationStreet}
        number={locationNumber}
        districtQuery={districtQuery}
        selectedDistrictUbigeo={selectedDistrictUbigeo}
        coverageDistricts={coverageDistricts}
        filteredCoverageDistricts={filteredCoverageDistricts}
        coverageLoading={coverageLoading}
        districtAutocompleteOpen={districtAutocompleteOpen}
        highlightedDistrictIndex={highlightedDistrictIndex}
        onStreetChange={setLocationStreet}
        onNumberChange={setLocationNumber}
        onDistrictQueryChange={handleDistrictQueryChange}
        onSelectDistrict={selectCoverageDistrict}
        onFocusDistrict={focusDistrictAutocomplete}
        onBlurDistrict={blurDistrictAutocomplete}
        onDistrictKeyDown={handleDistrictKeyDown}
        onSetHighlightedDistrictIndex={setHighlightedDistrictIndex}
        onSaveAddress={saveManualPlazaLocation}
        onUseCurrentLocation={useCurrentBrowserLocation}
        saveAddressPending={setLocationMutation.isPending}
        geoResolving={geoResolving}
        locationError={locationError}
        activeDistrictOptionId={activeDistrictOptionId}
      />

      {/* Disconnect-confirm dialog lifted out of <StoreSheet /> so the two
          dialogs are DOM-level siblings rather than illegally nested. */}
      <DisconnectConfirmDialog
        store={store}
        open={confirmDisconnectOpen}
        onOpenChange={setConfirmDisconnectOpen}
        onConfirm={handleStoreDisconnect}
        pending={disconnectMutation.isPending}
      />

      <StoreProductPicker
        store={store}
        item={selectedPickerItem}
        mapping={selectedPickerItem ? storeMappings.get(selectedPickerItem.name) ?? null : null}
        otherStoreMapping={selectedPickerItem ? otherStoreMappings.get(selectedPickerItem.name) ?? null : null}
        existingCartSkuId={
          selectedPickerItem
            ? (() => {
                const m = storeMappings.get(selectedPickerItem.name);
                if (!m) return null;
                return cartItemsBySku.has(m.skuId) ? m.skuId : null;
              })()
            : null
        }
        weekId={state.currentWeekId}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onCartResult={(message) => showToast({ message, tone: "success" })}
        onCartChanged={() => {
          // The picker calls this after a cart-mutating save. Auto-check the
          // ingredient — the user just "took care of it" by adding to cart.
          if (selectedPickerItem) markBought(selectedPickerItem.name);
        }}
      />

      <StoreExtrasDialog
        store={store}
        weekId={state.currentWeekId}
        open={extrasDialogOpen}
        onOpenChange={setExtrasDialogOpen}
        onAdded={(message) => showToast({ message, tone: "success" })}
      />

      <ComprasToast toast={toast} onDismiss={dismissToast} />
      {logoutDialog}
    </div>
  );
}
